// Assessment Status Caching Utilities

import { ASSESSMENT_STATUS_CONFIG } from "../config/assessmentStatusConfig";
import type {
  StatusCacheEntry,
  AssessmentCompletionResponse,
} from "../types/assessmentStatusTypes";

export class StatusCache {
  private static cache = new Map<string, StatusCacheEntry>();

  /**
   * Generate cache key based on token
   */
  private static generateCacheKey(token: string): string {
    // Use token hash to create consistent key
    return `status_${btoa(token.substring(0, 20))}`;
  }

  /**
   * Get cached status response
   */
  static get(token: string): AssessmentCompletionResponse | null {
    const key = this.generateCacheKey(token);
    const cached = this.cache.get(key);

    if (!cached) {
      return null;
    }

    // Check if expired
    const now = Date.now();
    if (now - cached.timestamp > ASSESSMENT_STATUS_CONFIG.CACHE.TTL) {
      this.cache.delete(key);
      return null;
    }

    return {
      ...cached.response,
      message: cached.response.message + " (from cache)",
    };
  }

  /**
   * Set cache entry
   */
  static set(token: string, response: AssessmentCompletionResponse): void {
    const key = this.generateCacheKey(token);

    // Manage cache size
    this.manageCacheSize();

    this.cache.set(key, {
      response: { ...response },
      timestamp: Date.now(),
      token: token.substring(0, 10) + "...", // Store partial for debugging
    });
  }

  /**
   * Clear cache for specific token
   */
  static clear(token: string): void {
    const key = this.generateCacheKey(token);
    this.cache.delete(key);
  }

  /**
   * Clear all cache
   */
  static clearAll(): void {
    this.cache.clear();
  }

  /**
   * Clean expired entries and manage size
   */
  private static manageCacheSize(): void {
    const now = Date.now();

    // Remove expired entries
    for (const [key, cached] of this.cache.entries()) {
      if (now - cached.timestamp > ASSESSMENT_STATUS_CONFIG.CACHE.TTL) {
        this.cache.delete(key);
      }
    }

    // Remove oldest if over limit
    if (this.cache.size >= ASSESSMENT_STATUS_CONFIG.CACHE.MAX_SIZE) {
      const oldestKey = this.cache.keys().next().value;
      if (oldestKey) {
        this.cache.delete(oldestKey);
      }
    }
  }

  /**
   * Get cache statistics
   */
  static getStats() {
    const now = Date.now();
    let validEntries = 0;
    let expiredEntries = 0;

    for (const cached of this.cache.values()) {
      if (now - cached.timestamp > ASSESSMENT_STATUS_CONFIG.CACHE.TTL) {
        expiredEntries++;
      } else {
        validEntries++;
      }
    }

    return {
      total: this.cache.size,
      valid: validEntries,
      expired: expiredEntries,
      maxSize: ASSESSMENT_STATUS_CONFIG.CACHE.MAX_SIZE,
      ttl: ASSESSMENT_STATUS_CONFIG.CACHE.TTL,
    };
  }

  /**
   * Force cleanup of expired entries
   */
  static cleanup(): void {
    this.manageCacheSize();
  }
}
